import { create } from "zustand";
import { t } from "@/i18n";
import type { User, LoginCredentials } from "@/types/auth";
import { authService } from "@/services/auth";
import { httpClient } from "@/services/api";
import { getToken, clearToken } from "@/utils/authToken";

interface AuthState {
  user: User | null;
  isLoading: boolean;
  error: string | null;
  sessionExpired: boolean;

  login: (credentials: LoginCredentials) => Promise<boolean>;
  logout: () => Promise<void>;
  initializeAuth: () => void;
  updateUser: (userData: Partial<User>) => void;
  clearError: () => void;
  setSessionExpired: (expired: boolean) => void;
}

const STORAGE_KEY = "auth_user";

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  isLoading: false,
  error: null,
  sessionExpired: false,

  login: async (credentials) => {
    set({ isLoading: true, error: null });

    // Clear any stale cache from an incomplete logout.
    try {
      httpClient.clearCache();
    } catch (err) {
      console.warn("[AUTH] Erro ao limpar caches antes do login:", err);
    }

    try {
      const response = await authService.login(credentials);

      if (response.success && response.user) {
        // O token (max_monitor_token) mantém a sessão; aqui só persistimos os
        // dados do usuário para reidratar no F5.
        localStorage.setItem(STORAGE_KEY, JSON.stringify(response.user));
        set({ user: response.user, isLoading: false });

        // O catálogo de tradução do conteúdo é carregado no boot, quando ainda
        // não há sessão — a chamada volta 401 e o mapa fica vazio. Recarrega
        // agora que há credencial, senão os títulos dos cards só apareceriam
        // traduzidos no F5 seguinte.
        void import("@/stores/dataI18nStore")
          .then((m) => m.useDataI18nStore.getState().reload())
          .catch(() => {
            /* catálogo é opcional: sem ele o texto original é exibido */
          });

        return true;
      }

      set({
        error: response.message || "Erro ao fazer login",
        isLoading: false,
      });
      return false;
    } catch (err) {
      set({
        error: err instanceof Error ? err.message : t("auth.unexpectedError"),
        isLoading: false,
      });
      return false;
    }
  },

  logout: async () => {
    set({ isLoading: true });

    try {
      await authService.logout();
    } catch (err) {
      console.error("Erro ao fazer logout:", err);
    } finally {
      localStorage.removeItem(STORAGE_KEY);

      // Cascade cleanup. Each block is guarded so a failure in one doesn't
      // skip the others.
      try {
        clearToken();
      } catch (err) {
        console.warn("[AUTH] Erro ao limpar token:", err);
      }

      try {
        httpClient.clearCache();
      } catch (err) {
        console.warn("[AUTH] Erro ao limpar httpClient cache:", err);
      }

      try {
        const { socketService } = await import("@/services/socketService");
        socketService.disconnect();
      } catch (err) {
        console.warn("[AUTH] Erro ao desconectar socket:", err);
      }

      try {
        const { queryClient } = await import("@/lib/queryClient");
        await queryClient.cancelQueries();
        queryClient.clear();
      } catch (err) {
        console.warn("[AUTH] Erro ao limpar TanStack Query cache:", err);
      }

      set({
        user: null,
        error: null,
        sessionExpired: false,
        isLoading: false,
      });
    }
  },

  initializeAuth: () => {
    const savedUser = localStorage.getItem(STORAGE_KEY);
    if (!savedUser) return;

    // Estado inconsistente: usuário persistido mas sem token (ex.: sessão
    // antiga por cookie de antes do deploy, ou localStorage parcial). Limpa e
    // força re-login para não renderizar "autenticado" e tomar 401 em tudo.
    if (!getToken()) {
      localStorage.removeItem(STORAGE_KEY);
      return;
    }

    try {
      const user = JSON.parse(savedUser) as User;
      set({ user });
    } catch (err) {
      console.error("Erro ao inicializar auth:", err);
      void get().logout();
    }
  },

  updateUser: (userData) => {
    const current = get().user;
    if (!current) return;
    const updated = { ...current, ...userData };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
    set({ user: updated });
  },

  clearError: () => set({ error: null }),
  setSessionExpired: (expired) => set({ sessionExpired: expired }),
}));

// Selectors — use these in components to subscribe to specific slices.
export const selectUser = (s: AuthState) => s.user;
export const selectIsAuthenticated = (s: AuthState) => !!s.user;
export const selectUserRole = (s: AuthState) => s.user?.role ?? "user";
export const selectUserName = (s: AuthState) => s.user?.name ?? t("auth.defaultUserName");
export const selectUserEmail = (s: AuthState) => s.user?.email ?? "";
export const selectUserIoTCompany = (s: AuthState) =>
  s.user?.iotCompany ?? "";
export const selectIsAdmin = (s: AuthState) =>
  s.user?.isAdmin === true || s.user?.role === "admin";
export const selectIsReadOnly = (s: AuthState) =>
  s.user?.isReadOnly === true || s.user?.role === "read-only";
